import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { Project } from '../types';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import ProjectsTableAgGrid from '../components/ProjectsTableAgGrid';
import ErrorBoundary from '../components/ErrorBoundary';
import { STANDALONE_PROPOSAL_CREATE_STATE } from '../utils/leadProjectCreate';

const ProposalsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';

  const { projects, loading, error } = useProjects();
  const { users } = useUsers();

  const handleProjectClick = useCallback((project: Project) => {
    navigate(`/projects/${project.id}/documents`);
  }, [navigate]);

  const canCreateProject = hasCapability(user, CAP.CREATE_PROJECT);

  const pageFillSx = {
    height: `calc(100vh - ${theme.spacing(10)} - ${theme.spacing(4)})`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  } as const;

  if (loading) {
    return (
      <Box sx={pageFillSx}>
        <Skeleton variant="rectangular" height="100%" />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <Box sx={pageFillSx}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} sx={{ flexShrink: 0 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}
          >
            Proposal
          </Typography>

          {canCreateProject && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/projects/create', { state: STANDALONE_PROPOSAL_CREATE_STATE })}
              aria-label="Create new proposal"
              sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
            >
              New Proposal
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, flexShrink: 0, borderRadius: 1, border: `1px solid ${panelBorder}` }} role="alert">
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ProjectsTableAgGrid
            projects={projects}
            users={users}
            scope="proposal"
            onProjectClick={handleProjectClick}
            onEditClick={(projectId) => navigate(`/projects/${projectId}/edit`)}
          />
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default ProposalsPage;
